/**
 * rFactor 2 race-template schema + settings catalogue.
 *
 * `RaceTemplate.content` (for `game === "rfactor-2"`) is the JSON serialisation
 * of `Rf2RaceTemplateContent`. It stores operator-friendly ABSOLUTE values
 * (e.g. `aiStrength: 95`, `fuelUsage: 1`), which the sim agent translates into
 * rF2's REST session settings.
 *
 * Why a catalogue: rF2's `POST /rest/sessions/settings` is a STEPPER, not an
 * absolute setter, and most settings are enums whose value↔label maps and
 * ranges are not discoverable from a single GET. The maps/ranges below were
 * captured from a live rF2 rig (build via `/rest/sessions`) and are the single
 * source of truth shared by:
 *   - the sim agent (field → `sessionKey`, applied via `setSessionSettingAbsolute`)
 *   - the hub-ui template form (control kind, options, min/max, labels)
 *
 * Every field is OPTIONAL: an absent field means "leave rF2's current value
 * untouched". This keeps templates additive and forward-compatible.
 */
/** Enum value↔label maps captured from a live rF2 rig. */
export const RF2_ENUMS = {
    fuelUsage: [
        { value: 0, label: "Off" }, { value: 1, label: "Normal" }, { value: 2, label: "X2" },
        { value: 3, label: "X3" }, { value: 4, label: "X4" }, { value: 5, label: "X5" },
        { value: 6, label: "X6" }, { value: 7, label: "X7" },
    ],
    tireWear: [
        { value: 0, label: "Off" }, { value: 1, label: "Normal" }, { value: 2, label: "X2" },
        { value: 3, label: "X3" }, { value: 4, label: "X4" }, { value: 5, label: "X5" },
        { value: 6, label: "X6" }, { value: 7, label: "X7" },
    ],
    mechFailures: [
        { value: 0, label: "Off" }, { value: 1, label: "Normal" }, { value: 2, label: "Time Scaled" },
    ],
    flagRules: [
        { value: 0, label: "None" }, { value: 1, label: "Black Only" },
        { value: 2, label: "Full" }, { value: 3, label: "Full w/o DQ" },
    ],
    blueFlags: [
        { value: 0, label: "None" },
        { value: 1, label: "Show but Never Penalize" },
        { value: 2, label: "Penalize within 0.3s" },
        { value: 3, label: "Penalize within 0.5s" },
        { value: 4, label: "Penalize within 0.7s" },
        { value: 5, label: "Penalize within 0.9s" },
        { value: 6, label: "Penalize within 1.1s" },
        { value: 7, label: "Use Default" },
    ],
    cutRules: [
        { value: 0, label: "None" }, { value: 1, label: "Default" },
        { value: 2, label: "Strict" }, { value: 3, label: "Relaxed" },
    ],
    weather: [
        { value: 0, label: "Sunny" }, { value: 1, label: "Cloudy" }, { value: 2, label: "Rainy" },
        { value: 3, label: "Default" }, { value: 4, label: "Scripted" },
    ],
    finishCriteria: [
        { value: -2, label: "% Track Laps" }, { value: -1, label: "% Track Time" },
        { value: 0, label: "% Track Default" }, { value: 1, label: "Laps" },
        { value: 2, label: "Time" }, { value: 3, label: "Laps and Time" },
    ],
    parcFerme: [
        { value: 0, label: "Off" },
        { value: 1, label: "No Changes Between Qualify And Race" },
        { value: 2, label: "No Changes Unless It Rains" },
        { value: 3, label: "Default" },
    ],
    formation: [
        { value: 0, label: "Standing" }, { value: 1, label: "Formation/Standing" },
        { value: 2, label: "Rolling" }, { value: 3, label: "Track" }, { value: 4, label: "Fast Rolling" },
    ],
    privateQualifying: [
        { value: 0, label: "Off" }, { value: 1, label: "On" }, { value: 2, label: "Default" },
    ],
};
/**
 * The full catalogue. Order = display order within each group. Drives both the
 * hub-ui form and the agent's apply step.
 *
 * NB: numOpponents is intentionally NOT here — it is applied via the dedicated
 * `setNumOpponents` path (selection must stay non-empty; clearing it crashes the
 * loader), and its value is mode-derived (0 for hotlap). It still lives on
 * Rf2RaceTemplateContent so a race template can carry a desired count.
 */
export const RF2_FIELDS = [
    // AI
    { field: "aiStrength", label: "AI Strength", group: "AI", kind: "percent", sessionKey: "SESSSET_AI_Strength", min: 10, max: 120 },
    { field: "aiAggression", label: "AI Aggression", group: "AI", kind: "percent", sessionKey: "SESSSET_AI_Aggression", min: 0, max: 100, floatScale: 100 },
    { field: "aiLimiter", label: "AI Limiter", group: "AI", kind: "percent", sessionKey: "SESSSET_AI_Limiter", min: 0, max: 100, floatScale: 100 },
    { field: "gridPosition", label: "Grid Position", group: "AI", kind: "gridPosition", sessionKey: "SESSSET_Grid_Position", min: 0, max: 59, help: "0 = Random" },
    // Format
    { field: "finishCriteria", label: "Finish Criteria", group: "Format", kind: "enum", sessionKey: "SESSSET_Finish_Criteria", options: RF2_ENUMS.finishCriteria },
    { field: "raceLaps", label: "Race Laps", group: "Format", kind: "int", sessionKey: "SESSSET_Race_Laps", min: 1, max: 200 },
    { field: "raceTimed", label: "Timed Race", group: "Format", kind: "bool", sessionKey: "SESSSET_race_time" },
    { field: "raceTimerSeconds", label: "Race Timer (s)", group: "Format", kind: "int", sessionKey: "SESSSET_race_timer", min: 0, max: 86400 },
    { field: "practiceLengthMins", label: "Practice Length (min)", group: "Format", kind: "int", sessionKey: "SESSSET_Practice_Length", min: 0, max: 1440 },
    { field: "qualifyLengthMins", label: "Qualify Length (min)", group: "Format", kind: "int", sessionKey: "SESSSET_Qualify_Length", min: 0, max: 1440 },
    { field: "qualifyLaps", label: "Qualify Laps", group: "Format", kind: "int", sessionKey: "SESSSET_Qualify_Laps", min: 0, max: 50 },
    { field: "runWarmup", label: "Run Warm-up", group: "Format", kind: "bool", sessionKey: "SESSSET_run_warmup" },
    { field: "warmupLengthMins", label: "Warm-up Length (min)", group: "Format", kind: "int", sessionKey: "SESSSET_WarmUp_Length", min: 0, max: 120 },
    // Realism
    { field: "fuelUsage", label: "Fuel Usage", group: "Realism", kind: "enum", sessionKey: "SESSSET_Fuel_Usage", options: RF2_ENUMS.fuelUsage },
    { field: "tireWear", label: "Tyre Wear", group: "Realism", kind: "enum", sessionKey: "SESSSET_Tire_Wear", options: RF2_ENUMS.tireWear },
    { field: "mechFailures", label: "Mechanical Failures", group: "Realism", kind: "enum", sessionKey: "SESSSET_Mech_Failures", options: RF2_ENUMS.mechFailures },
    { field: "damageMultiplier", label: "Damage", group: "Realism", kind: "percent", sessionKey: "SESSSET_Damage_Multi", min: 0, max: 100 },
    // Rules
    { field: "flagRules", label: "Flag Rules", group: "Rules", kind: "enum", sessionKey: "SESSSET_flag_rules", options: RF2_ENUMS.flagRules },
    { field: "blueFlags", label: "Blue Flags", group: "Rules", kind: "enum", sessionKey: "SESSSET_blue_flags", options: RF2_ENUMS.blueFlags },
    { field: "cutsAllowed", label: "Cuts Allowed", group: "Rules", kind: "int", sessionKey: "SESSSET_cuts_allowed", min: 0, max: 99 },
    { field: "cutRules", label: "Cut Rules", group: "Rules", kind: "enum", sessionKey: "SESSSET_cut_rules", options: RF2_ENUMS.cutRules },
    { field: "parcFerme", label: "Parc Fermé", group: "Rules", kind: "enum", sessionKey: "SESSSET_parc_ferme", options: RF2_ENUMS.parcFerme },
    { field: "formation", label: "Formation / Start", group: "Rules", kind: "enum", sessionKey: "SESSSET_formation", options: RF2_ENUMS.formation },
    { field: "privateQualifying", label: "Private Qualifying", group: "Rules", kind: "enum", sessionKey: "SESSSET_private_qual", options: RF2_ENUMS.privateQualifying },
    // Weather
    { field: "weather", label: "Weather", group: "Weather", kind: "enum", sessionKey: "SESSSET_weather", options: RF2_ENUMS.weather },
    { field: "timescaledWeather", label: "Dynamic Weather", group: "Weather", kind: "bool", sessionKey: "SESSSET_timescaled_weather" },
    { field: "realRoadTimescaleRace", label: "RealRoad Time-scale (Race)", group: "Weather", kind: "int", sessionKey: "SESSSET_realroad_timescale_race", min: 0, max: 100 },
];
/** Ordered groups for the editor. */
export const RF2_FIELD_GROUPS = ["AI", "Format", "Realism", "Rules", "Weather"];
//# sourceMappingURL=rf2-template.js.map